<template>
  <mds-section border="tertiary" bold :size="8" :title="translate('planFee')">
    <template v-slot:mds-section-actions>
      <mds-switch v-model="includePlanFee" hidden-label @change="togglePlanFee">
        {{ translate('include') }}
      </mds-switch>
    </template>
    <template v-slot:default>
      <mds-form v-if="includePlanFee" size="small">
        <mds-fieldset size="small" variation="radio-group" :legend="translate('selectPlanFee')">
          <mds-radio-button
            v-for="(item, index) in planFeeOptions"
            :key="index"
            v-model="planFeeType"
            name="planFeeType"
            size="small"
            :value="item.value"
            :disabled="isPlanFeeDisabled(item.value)"
            @input="handlePlanFeeTypeChanged"
          >
            {{ item.text }}
            <template v-slot:mds-microcopy>
              <template v-if="item.value === 0">
                <mds-input
                  readonly
                  hidden-label
                  :class="$style['read-only-input']"
                  :label="translate('estimatedPercent')"
                  :disabled="item.value !== planFeeType"
                  :value="estimatedPlanFee !== null ? estimatedPlanFee : renderEmDash()"
                >
                </mds-input>
              </template>
              <template v-else-if="item.value === 1">
                <mds-input
                  readonly
                  hidden-label
                  :class="$style['read-only-input']"
                  :label="translate('benchmarkPercent')"
                  :disabled="item.value !== planFeeType"
                  :value="benchmarkPlanFee !== null ? benchmarkPlanFee : renderEmDash()"
                >
                </mds-input>
              </template>
              <template v-else>
                <mds-input
                  hidden-label
                  :label="translate('customPercent')"
                  :disabled="item.value !== planFeeType"
                  :value="customPlanFee"
                  @change="handleCustomPlanFeeChanged"
                >
                </mds-input>
              </template>
            </template>
          </mds-radio-button>
        </mds-fieldset>
      </mds-form>
    </template>
  </mds-section>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import MdsSection from '@mds/section';
import MdsSwitch from '@mds/switch';
import MdsForm from '@mds/form';
import MdsFieldset from '@mds/fieldset';
import MdsRadioButton from '@mds/radio-button';
import MdsInput from '@mds/input';
import { parseNumber, isInvalidPercentage, renderEmDash } from '../../utils/utils';
import { planFeeTypes } from '../../utils/input-options';
import { PLAN_FEE_TYPES } from '../../utils/constants';

const { ESTIMATED_PERCENT, BENCHMARK_PERCENT, CUSTOM_PERCENT } = PLAN_FEE_TYPES;

export default {
  name: 'plan-fee',
  components: {
    MdsSection,
    MdsSwitch,
    MdsForm,
    MdsFieldset,
    MdsRadioButton,
    MdsInput,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    planFeeSetting: {
      type: Object,
    },
  },
  data() {
    return {
      includePlanFee: false,
      isUserDCPlan: false,
      planFeeType: null,
      estimatedPlanFee: null,
      benchmarkPlanFee: null,
      customPlanFee: 0,
    };
  },
  computed: {
    planFeeOptions() {
      const planFeeOptions = this.translateComponentOptions(planFeeTypes);
      if (this.isUserDCPlan) {
        return planFeeOptions.filter(item => item.value === 2);
      }
      return planFeeOptions;
    },
  },
  watch: {
    planFeeSetting() {
      this.initPlanFeeData();
    },
  },
  created() {
    this.initPlanFeeData();
  },
  methods: {
    initPlanFeeData() {
      const {
        includePlanFee = false,
        isUserDCPlan = false,
        estimatedPlanFee = null,
        benchmarkPlanFee = null,
        customPlanFee = 0,
      } = this.planFeeSetting || {};
      let planFeeType;
      if (estimatedPlanFee !== null) {
        planFeeType = ESTIMATED_PERCENT;
      } else if (benchmarkPlanFee !== null) {
        planFeeType = BENCHMARK_PERCENT;
      } else {
        planFeeType = CUSTOM_PERCENT;
      }
      this.includePlanFee = includePlanFee;
      this.isUserDCPlan = isUserDCPlan;
      this.planFeeType = isUserDCPlan ? CUSTOM_PERCENT : planFeeType;
      this.estimatedPlanFee = estimatedPlanFee;
      this.benchmarkPlanFee = benchmarkPlanFee;
      this.customPlanFee = customPlanFee;
    },
    togglePlanFee() {
      this.emitPlanFeeData();
    },
    isPlanFeeDisabled(planFeeType) {
      return (
        (planFeeType === ESTIMATED_PERCENT && this.estimatedPlanFee === null) ||
        (planFeeType === BENCHMARK_PERCENT && this.benchmarkPlanFee === null)
      );
    },
    renderEmDash() {
      return renderEmDash();
    },
    translateComponentOptions(options) {
      return options.map(({ labelKey, value }) => {
        return {
          text: this.translate(labelKey),
          value,
        };
      });
    },
    handlePlanFeeTypeChanged() {
      this.emitPlanFeeData();
    },
    handleCustomPlanFeeChanged(e) {
      const percentage = parseNumber(e.target.value);
      if (isInvalidPercentage(percentage) || percentage === this.customPlanFee) {
        e.target.value = this.customPlanFee;
      } else {
        this.customPlanFee = percentage;
        this.emitPlanFeeData();
      }
    },
    emitPlanFeeData() {
      let planFee;
      if (this.planFeeType === ESTIMATED_PERCENT) {
        planFee = this.estimatedPlanFee;
      } else if (this.planFeeType === BENCHMARK_PERCENT) {
        planFee = this.benchmarkPlanFee;
      } else {
        planFee = this.customPlanFee;
      }
      this.$emit('plan-fee-changed', {
        includePlanFee: this.includePlanFee,
        planFeeType: this.planFeeType,
        planFee,
      });
    },
  },
};
</script>

<style lang="scss" module>
.read-only-input {
  .mds-input[readonly] {
    box-shadow: none;
  }
}
</style>
